import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import React, { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import {
  Button,
  Card,
  Icon,
  PageHeader,
  SegmentedControl,
  styles as ui,
  useToast,
} from "stories/Admin";
import { JamatResponse, PrayerTimes } from "types/JamatPageTypes";
import styles from "./JamatPage.module.scss";
import JamatPageSkeleton from "./JamatPageSkeleton/JamatPageSkeleton";
import JamatPeriodForm from "./JamatPeriodForm/JamatPeriodForm";
import {
  JamatPrayer,
  MONTHS_LONG,
  MONTHS_SHORT,
  salahBaseFrom,
} from "./jamatPeriodUtils";
import PrayerTimeRow from "./PrayerTimeRow/PrayerTimeRow";

const JamatPage: React.FC = () => {
  const { orgNumber, selectedOrgNumber, mosqueName, selectedOrgName } =
    useAuth();
  const toast = useToast();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [editTimes, setEditTimes] = useState<Record<string, PrayerTimes>>({});
  /** Bønnetiden per dag og bønn, så «+7 min» kan vises som klokkeslettet appene og TV-ene viser. */
  const [salahBase, setSalahBase] = useState<
    Record<string, Partial<Record<JamatPrayer, string>>>
  >({});
  const [csvData, setCsvData] = useState<any[]>([]);
  const { isLoading, execute } = useFetch<JamatResponse[]>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const orgNr = orgNumber || "";

  const fetchPrayerTimes = async () => {
    const month = (currentMonth.getMonth() + 1).toString().padStart(2, "0");
    const url = `/api/v1/jamat/${selectedOrgNumber}/${month}`;

    const result = await execute(url, "GET");
    if (result) {
      const newPrayerTimesData = result.reduce<Record<string, PrayerTimes>>(
        (acc, item) => {
          acc[item.startDate] = mapToPrayerTimes(item);
          return acc;
        },
        {},
      );
      setEditTimes(newPrayerTimesData);
      setSalahBase(
        Object.fromEntries(
          Object.entries(newPrayerTimesData).map(([key, times]) => [
            key,
            salahBaseFrom(times as unknown as Record<string, unknown>),
          ]),
        ),
      );
    }
  };

  const formatPrayerTime = (time: string) =>
    time === "00:00" || !time ? "" : time;

  const mapToPrayerTimes = (item: JamatResponse): PrayerTimes => ({
    id: item.id,
    fajr: formatPrayerTime(item.fajr),
    duhr: formatPrayerTime(item.duhr),
    asr: formatPrayerTime(item.asr),
    maghrib: formatPrayerTime(item.maghrib),
    isha: formatPrayerTime(item.isha),
    fajrJamatOffset: item.fajrJamatOffset ?? -1,
    duhrJamatOffset: item.duhrJamatOffset ?? -1,
    asrJamatOffset: item.asrJamatOffset ?? -1,
    maghribJamatOffset: item.maghribJamatOffset ?? -1,
    ishaJamatOffset: item.ishaJamatOffset ?? -1,
  });

  useEffect(() => {
    fetchPrayerTimes();
  }, [currentMonth, selectedOrgNumber]);

  const fetchCsvData = async () => {
    const url = `/api/v1/jamat/${selectedOrgNumber}/download-csv`;
    try {
      const result = await execute(url, "GET");
      setCsvData(result || []);
    } catch (error) {
      console.error("Failed to fetch CSV data", error);
    }
  };

  useEffect(() => {
    fetchCsvData();
  }, []);

  const handleMonthChange = (monthIndex: number) => {
    const newMonth = new Date(currentMonth);
    newMonth.setDate(1);
    newMonth.setMonth(monthIndex);
    setCurrentMonth(newMonth);
  };

  const handlePrayerTimeChange = (
    dateKey: string,
    prayer: "fajr" | "duhr" | "asr" | "maghrib" | "isha",
    time: string,
  ) => {
    setEditTimes((prevEditTimes) => ({
      ...prevEditTimes,
      [dateKey]: {
        ...prevEditTimes[dateKey],
        [prayer]: time,
      },
    }));
  };

  const handleOffsetChange = (
    dateKey: string,
    offset:
      | "fajrJamatOffset"
      | "duhrJamatOffset"
      | "asrJamatOffset"
      | "maghribJamatOffset"
      | "ishaJamatOffset",
    value: number,
  ) => {
    setEditTimes((prevEditTimes) => ({
      ...prevEditTimes,
      [dateKey]: {
        ...prevEditTimes[dateKey],
        [offset]: value,
      },
    }));
  };

  const savePrayerTime = async (dateKey: string) => {
    const updatedTimes = editTimes[dateKey];
    if (updatedTimes.id) {
      await updatePrayerTime(dateKey, updatedTimes);
    } else {
      await createPrayerTime(dateKey, updatedTimes);
    }
  };

  const createPrayerTime = async (dateKey: string, times: PrayerTimes) => {
    const requestBody = {
      ...times,
      startDate: dateKey,
      mosqueOrgNr: selectedOrgNumber,
      fajrJamatOffset: times.fajrJamatOffset ?? -1,
      duhrJamatOffset: times.duhrJamatOffset ?? -1,
      asrJamatOffset: times.asrJamatOffset ?? -1,
      maghribJamatOffset: times.maghribJamatOffset ?? -1,
      ishaJamatOffset: times.ishaJamatOffset ?? -1,
    };

    const url = `/api/v1/jamat`;
    try {
      await execute(url, "POST", requestBody);
      fetchPrayerTimes();
    } catch (error) {
      console.error("Failed to create prayer time", error);
    }
  };

  const updatePrayerTime = async (dateKey: string, times: PrayerTimes) => {
    const url = `/api/v1/jamat/${selectedOrgNumber}/${times.id}`;
    try {
      await execute(url, "PATCH", times);
      fetchPrayerTimes(); // Refresh times after update
    } catch (error) {
      console.error("Failed to update prayer time", error);
    }
  };

  const handleSaveAllRows = async () => {
    const payload = Object.entries(editTimes).map(([startDate, times]) => ({
      id: times.id || 0,
      startDate,
      endDate: startDate, // Assuming single day for now
      fajr: times.fajr,
      duhr: times.duhr,
      asr: times.asr,
      maghrib: times.maghrib,
      isha: times.isha,
      mosqueOrgNr: selectedOrgNumber,
      fajrJamatOffset: times.fajrJamatOffset ?? -1,
      duhrJamatOffset: times.duhrJamatOffset ?? -1,
      asrJamatOffset: times.asrJamatOffset ?? -1,
      maghribJamatOffset: times.maghribJamatOffset ?? -1,
      ishaJamatOffset: times.ishaJamatOffset ?? -1,
    }));

    try {
      const response = await execute("/api/v1/jamat/batch", "POST", payload);
      console.log("All rows saved successfully:", response);
      toast.success("Alle jamat-tider er lagret.");
      fetchPrayerTimes(); // Refresh after save
    } catch (error) {
      console.error("Failed to save prayer times:", error);
      toast.error("Kunne ikke lagre jamat-tidene. Prøv igjen.");
    }
  };

  const deletePrayerTime = async (dateKey: string) => {
    const updatedTimes = editTimes[dateKey];
    const url = `/api/v1/jamat/${updatedTimes.id}`;
    try {
      await execute(url, "DELETE");
      fetchPrayerTimes();
    } catch (error) {
      console.error("Failed to delete prayer time", error);
    }
  };

  const renderPrayerTimes = () => {
    const daysInMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      0,
    ).getDate();
    const rows = [];
    let previousTimes: PrayerTimes | null = null;

    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${day.toString().padStart(2, "0")}-${(
        currentMonth.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}`;
      const times = editTimes[dateKey] || {
        id: 0,
        fajr: "",
        duhr: "",
        asr: "",
        maghrib: "",
        isha: "",
        fajrJamatOffset: -1,
        duhrJamatOffset: -1,
        asrJamatOffset: -1,
        maghribJamatOffset: -1,
        ishaJamatOffset: -1,
      };

      const dayLabel = day.toString().padStart(2, "0");
      const formattedDate = `${dayLabel}. ${MONTHS_LONG[currentMonth.getMonth()]}`;
      const shortDate = `${dayLabel}. ${MONTHS_SHORT[
        currentMonth.getMonth()
      ].toLowerCase()}`;
      const isFriday =
        new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth(),
          day,
        ).getDay() === 5;

      rows.push(
        <PrayerTimeRow
          key={dateKey}
          dateKey={dateKey}
          times={times}
          formattedDate={formattedDate}
          shortDate={shortDate}
          isFriday={isFriday}
          previousTimes={previousTimes}
          salahBase={salahBase[dateKey]}
          onPrayerTimeChange={handlePrayerTimeChange}
          onOffsetChange={handleOffsetChange}
          onSave={savePrayerTime}
          onCreate={createPrayerTime}
          onDelete={deletePrayerTime}
          fetchLatestPrayerTimes={fetchPrayerTimes}
        />,
      );

      previousTimes = times;
    }

    return (
      <div className={ui.tblWrap}>
        <table className={ui.tbl}>
          <caption className={ui.srOnly}>
            Jamat-tider for {MONTHS_LONG[currentMonth.getMonth()]}
          </caption>
          <thead>
            <tr>
              <th scope="col">Dato</th>
              <th scope="col">Fajr</th>
              <th scope="col">Dhuhr</th>
              <th scope="col">Asr</th>
              <th scope="col">Maghrib</th>
              <th scope="col">Isha</th>
              <th scope="col">
                <span className={ui.srOnly}>Handlinger</span>
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    );
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const uploadUrl = `/api/v1/jamat/${selectedOrgNumber}/upload-csv`;
        await execute(uploadUrl, "POST", formData);
        console.log("File uploaded successfully");
        fetchPrayerTimes();
      } catch (error) {
        console.error("File upload failed", error);
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const saveAllButton = (
    <Button
      icon={<Icon name="check" size={16} strokeWidth={2.2} />}
      onClick={handleSaveAllRows}
    >
      Lagre alle
    </Button>
  );

  return (
    <>
      <PageHeader
        crumb={mosqueName || selectedOrgName || undefined}
        title="Jamat-tider"
        subtitle="Tidene for fellesbønn vises i appen, på TV-skjermen og i nedtellingen."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="upload" size={16} strokeWidth={2} />}
              onClick={triggerFileInput}
            >
              Importer CSV
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              className={styles.fileInput}
              accept=".csv"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(event) => handleFileUpload(event)}
            />
            <CSVLink
              data={csvData}
              filename={`jamat_times_${selectedOrgNumber}.csv`}
              className={`${ui.btn} ${ui.secondary}`}
            >
              <Icon name="download" size={16} strokeWidth={2} />
              Eksporter CSV
            </CSVLink>
            {saveAllButton}
          </>
        }
      />

      <JamatPeriodForm
        mosqueOrgNr={selectedOrgNumber ?? ""}
        onSaved={fetchPrayerTimes}
      />

      <Card title="Måned for måned">
        <div className={styles.monthRow}>
          <SegmentedControl
            label="Velg måned"
            options={MONTHS_SHORT.map((name, index) => ({
              value: index,
              label: name,
            }))}
            value={currentMonth.getMonth()}
            onChange={handleMonthChange}
            className={styles.months}
          />
          <span className={styles.help}>
            Endringer lagres med «Lagre alle». «+5 min» betyr offset fra
            bønnetiden.
          </span>
        </div>

        {isLoading ? <JamatPageSkeleton /> : renderPrayerTimes()}

        <div className={styles.footer}>{saveAllButton}</div>
      </Card>
    </>
  );
};

export default JamatPage;
